import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../../components/shared/Pill';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { PermissionDetail } from '../../../../app/services/api/generated';

type Props = {
  list: PermissionDetail[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
};

const PermissionsList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
}) => {
  const { t } = useTranslation('admin');

  if (totalCount === 0 && !isLoading) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('permissions.noPermissionsFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('permissions.title')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('permissions.title')}`} />}
        </Box>
      </Box>

      {isLoading ? (
        <TableLoading
          headerHeight={45}
          bodyRowHeight={40}
        />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('permissions.id')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('permissions.name')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('permissions.description')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell>{permission.id}</TableCell>
                      <TableCell>{permission.name}</TableCell>
                      <TableCell>{permission.description || '-'}</TableCell>
                    </TableRow>
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('permissions.noPermissionsFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default PermissionsList;
